// Hooks that consume API endpoints
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
  getDiagnosisList,
  getRequirementMedicalCenters,
  getRequirements,
  getRequirementTypes,
} from "../services/requirements";
import {
  getCentersFromRequirements,
  getClientRequirementsFromCenter,
  getClientRequirementsListFromCenter,
  getMedicalCenter,
  getMedicalCenters,
  getMedicalCenterFund,
  getRequirementsFromCenter,
  getManagedMedicalCenters,
  getCanAddRequirementsToCase,
} from "../services/medicalCenters";
import {
  getScaleRequirements,
  getScaleRequirementsForEdit,
  getScaleRequirementsHistory,
  getScales,
} from "../services/baremos";
import { getClient, getClients, getSubsidiaries } from "../services/clients";
import { DAYS, UserRole } from "../constants";
import { getBanks } from "../services/banks";
import { getAdvancesFromMedicalCenter } from "../services/advances";
import {
  getCaseById,
  getCases,
  getCasesSubscribe,
  getCasesCount,
  getMedicalCaseStatusHistory,
  getStaleMedicalCases,
  getPendingRequests,
} from "../services/medicalCases";
import { getCurrentDollarRate } from "../services/dollar";
import { getReportMedicalCases } from "../services/reports";
import { getInsurance } from "../services/insurance";
import {
  getusersList,
  getUsersClientList,
  getProfilePicture,
  canAddRequirementsToCaseCallCenter,
} from "../services/users";
import { fetchDashboardVersion } from "../services/version";
import { getStateLocations } from "../services/locations";

// Medical Staff
import {
  getCitaMedicaDoctors,
  getCitaMedicaSpecialties,
  getConsultDoctors,
  getCitaMedicaBookings,
} from "../services/medicalStaff";

import {
  getBillingList,
  getBillingAmountBs,
  getBillingDetail,
  getIsInsolvent,
  getBillingStats,
  getBillingHistory,
} from "../services/billing";

// @hector
import { getCachedRequirementsByClientAndCenter } from "../services/requirementMedicalCenterCache";
import { getClientList } from "../services/requirementCached";
import { getChatMessages } from "../services/chat";
import { getDependencyInsured } from "../services/rms";

/**
 *
 * Hook get version of the API
 */
export function useVersion() {
  return useQuery({
    queryKey: ["version"],
    queryFn: () => fetchDashboardVersion(),
    // process.env.REACT_APP_AUTH_URL
    // queryFn: () => fetch(`${import.meta.env.VITE_API_URL}version/`).then((res) => res.json()),
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Hook to get requirement types
 */
export function useRequirementTypes() {
  return useQuery({
    queryKey: ["requirementTypes"],
    queryFn: () => getRequirementTypes(),
    staleTime: 1000 * 60 * 60,
  });
}

/**
 * Hook to get all requirements, or filtered by type
 * @param {number | undefined} requirementType
 * @param {import("@tanstack/react-query").UseQueryOptions} options
 */
export function useRequirements(requirementType, options = {}) {
  return useQuery({
    queryKey: ["requirements", requirementType],
    queryFn: () => getRequirements(requirementType),
    ...options,
  });
}

/**
 * Hook to get all requirements, or filtered by type and companies
 * @param {number | undefined} requirementType
 * @param {number[]} companies
 */
export function useRequirementsCompanies(
  requirementType,
  companies = [],
  state = null
) {
  return useQuery({
    queryKey: ["requirements", requirementType, ...companies, state],
    queryFn: () => getRequirements(requirementType, companies, state),
    enabled: !!companies.length,
  });
}

/**
 * Hook to get all medical centers
 */
export function useMedicalCenters({ clientID, enabled = true }) {
  return useQuery({
    queryKey: ["medicalCenters" + clientID],
    queryFn: () => getMedicalCenters({ clientID }),
    enabled: enabled,
  });
}

/**
 * Hook to get all medical centers
 */
export function useMedicalCenterFund({
  clientID,
  state,
  select,
  enabled = undefined,
  forBalance = false,
}) {
  return useQuery({
    queryKey: ["medicalCenterFund" + clientID + state, { forBalance }],
    queryFn: () => getMedicalCenterFund({ clientID, state, forBalance }),
    enabled: enabled === undefined ? !!clientID : enabled,
    select: select,
  });
}

/**
 * Hook to get a single medical center
 * @param {number} centerId
 */
export function useSingleMedicalCenter(centerId, options = {}) {
  return useQuery({
    queryKey: ["medicalCenter", centerId],
    queryFn: () => getMedicalCenter(centerId),
    staleTime: Infinity,
    ...options,
  });
}

/**
 * Hook to get all medical cases
 */
export function useMedicalCases({ page, pageSize, filters }) {
  return useQuery({
    queryKey: ["medicalCases", page, pageSize, filters],
    queryFn: () => getCases(page, pageSize, filters),
    select: (data) => {
      return {
        count: data.count,
        results: data.results,
        // .map((medicalCase) => {
        //   // Get unique requirement types
        //   // const requirementTypesSet = new Set();
        //   // medicalCase.requirements.forEach((req) =>
        //   //   requirementTypesSet.add(req.type_requirement)
        //   // );
        //   // // Get all requirement names
        //   // const requirementNameArray = [];
        //   // medicalCase.requirements.forEach((req) =>
        //   //   requirementNameArray.push(req.requirement_name)
        //   // );
        //   return {
        //     ...medicalCase,
        //     // requirement_type_names: Array.from(requirementTypesSet).join(", "),
        //     // requirement_names: requirementNameArray.join(", "),
        //   };
        // }),
      };
    },
  });
}

// export function useMedicalCasesAll({ page, pageSize, filters, enabled }) {
//   return useQuery({
//     queryKey: ["medical-all"],
//     queryFn: () => getAllCases(page, pageSize, filters, true),
//     enabled: enabled,
//     select: (data) => {
//       data.results = data.results.map((medicalCase, index) => ({
//         ...medicalCase,
//         index: index + 1,
//       }));

//       return data;
//     },
//   });
// }

/**
 * Hook to get count medical cases
 */
export function useMedicalCasesCount() {
  return useQuery({
    queryKey: ["medicalCasesCount"],
    queryFn: () => getCasesCount(),
    select: (data) => data,
  });
}

/**
 * Hook to get all medical cases
 */
export function useMedicalCasesSubscribe({ page, pageSize, filters }) {
  return useQuery({
    queryKey: ["medicalCasesSubscribe", page, pageSize, filters],
    queryFn: () => getCasesSubscribe(page, pageSize, filters),
    select: (data) => {
      return {
        count: data?.count,
        results: data?.results?.map((medicalCase) => {
          // Get unique requirement types
          const requirementTypesSet = new Set();
          medicalCase.requirements.forEach((req) =>
            requirementTypesSet.add(req.type_requirement)
          );
          // Get all requirement names
          const requirementNameArray = [];
          medicalCase.requirements.forEach((req) =>
            requirementNameArray.push(req.requirement_name)
          );
          return {
            ...medicalCase,
            requirement_type_names: Array.from(requirementTypesSet).join(", "),
            requirement_names: requirementNameArray.join(", "),
          };
        }),
      };
    },
  });
}

export function useMedicalCasesSubscribeAll({
  page,
  pageSize,
  filters,
  enabled,
}) {
  return useQuery({
    queryKey: ["medicalCaseSubscribeAll", page, pageSize, filters],
    queryFn: () => getCasesSubscribe(page, pageSize, filters, true),
    enabled: enabled,
    select: (data) => {
      data.results = data.results.map((medicalCase, index) => ({
        ...medicalCase,
        index: index + 1,
      }));

      return data;
    },
  });
}

/**
 * Hook to get all medical cases
 */
export function useReportMedicalCase({ page, pageSize, filters }) {
  return useQuery({
    queryKey: ["medicalCasesReport", page, pageSize, filters],
    queryFn: () => getReportMedicalCases(page, pageSize, filters),
    select: (data) => {
      return {
        count: data.count,
        results: data.results.map((medicalCase) => {
          // Get unique requirement types
          // const requirementTypesSet = new Set();
          // medicalCase.requirements.forEach((req) =>
          //   requirementTypesSet.add(req.type_requirement)
          // );
          // // Get all requirement names
          // const requirementNameArray = [];
          // medicalCase.requirements.forEach((req) =>
          //   requirementNameArray.push(req.requirement_name)
          // );
          return {
            ...medicalCase,
            // requirement_type_names: Array.from(requirementTypesSet).join(", "),
            // requirement_names: requirementNameArray.join(", "),
          };
        }),
      };
    },
  });
}

export function useReportMedicalCaseAll({ page, pageSize, filters, enabled }) {
  return useQuery({
    queryKey: ["medicalCasesReportAll", page, pageSize, filters],
    queryFn: () => getReportMedicalCases(page, pageSize, filters, true),
    enabled: enabled,
    select: (data) => {
      data.results = data.results.map((medicalCase, index) => ({
        ...medicalCase,
        index: index + 1,
      }));

      return data;
    },
  });
}

/**
 * Hook to get all medical cases
 */
// export function useMedicalCasesSubscribe() {
//   return useQuery({
//     queryKey: ["medicalCasesSubscribe"],
//     queryFn: () => getCasesSubscribe(),
//     select: (data) => data.map((medicalCase) => {
//       // Get unique requirement types
//       const requirementTypesSet = new Set();
//       medicalCase.requirements.forEach((req) => requirementTypesSet.add(req.type_requirement));
//       // Get all requirement names
//       const requirementNameArray = [];
//       medicalCase.requirements.forEach((req) => requirementNameArray.push(req.requirement_name));
//       return {
//         ...medicalCase,
//         requirement_type_names: Array.from(requirementTypesSet).join(", "),
//         requirement_names: requirementNameArray.join(", "),
//       };
//     })
//   });
// }

/**
 * Hook to get single medical case
 * @param {number | string} caseId
 */
export function useSingleMedicalCase(caseId) {
  return useQuery({
    queryKey: ["medicalCase", Number(caseId)],
    queryFn: () => getCaseById(Number(caseId)),
    select: (data) => ({
      ...data,
      requirements: data.requirements.map((req) => ({
        done: false,
        ...req,
      })),
    }),
  });
}

/**
 * Hook to get stale medical cases
 * @author gabrielm
 */
export function useStaleMedicalCases({ page, pageSize, filters }) {
  return useQuery({
    queryKey: ["medicalCases", "stale", page, pageSize, filters],
    queryFn: () => getStaleMedicalCases(page, pageSize, filters),
    select: (data) => {
      return {
        count: data.count,
        results: data.results.map((medicalCase) => {
          // Get unique requirement types
          const requirementTypesSet = new Set();
          medicalCase.requirements.forEach((req) =>
            requirementTypesSet.add(req.type_requirement)
          );
          // Get all requirement names
          const requirementNameArray = [];
          medicalCase.requirements.forEach((req) =>
            requirementNameArray.push(req.requirement_name)
          );
          return {
            ...medicalCase,
            requirement_type_names: Array.from(requirementTypesSet).join(", "),
            requirement_names: requirementNameArray.join(", "),
          };
        }),
      };
    },
  });
}

/**
 * Hook to get medical centers for a requirement
 * @param {number | undefined} requirement
 */
export function useRequirementMedicalCenters(requirement) {
  return useQuery({
    queryKey: ["reqMedicalCenters", requirement],
    queryFn: () => getRequirementMedicalCenters(requirement),
  });
}

/**
 * @typedef MedicalCenterRequirement
 * @property {number} id
 * @property {object} client
 * @property {number} client.id
 * @property {string} client.name
 * @property {string} company_client_name
 * @property {number} requirement
 * @property {string} requirement_name
 * @property {string} type_requirement_name
 * @property {number | string} price_ref
 * @property {object[]} days
 */

/**
 * Hook to get requirements associated to a medical center
 * @param {number} centerId
 */
export function useMedicalCentersRequirements(centerId) {
  return useQuery({
    queryKey: ["medicalCenter", "requirements", Number(centerId)],
    queryFn: () => getRequirementsFromCenter(centerId),
    select: (data) =>
      data.map((req) => {
        /**@type {MedicalCenterRequirement} */
        const formattedData = {
          id: req.id,
          client: { id: req.company_client, name: req.company_client_name },
          company_client_name: req.company_client_name,
          requirement: req.requirement,
          requirement_name: req.requirement_name,
          type_requirement_name: req.type_requirement_name,
          price_ref: req.requirement_price_ref,
          days: req.requirement_day.map((reqDay) => ({
            ...DAYS.find((day) => day.apiLabel === reqDay.day),
            slots: reqDay.quota,
          })),
        };
        return formattedData;
      }),
  });
}

/**
 * Hook to get all available banks
 */
export function useBanks() {
  return useQuery({
    queryKey: ["banks"],
    queryFn: () => getBanks(),
    staleTime: Infinity,
  });
}

/**
 * Hook to get all advances corresponding to a specific medical center
 * @param {number} centerId
 * @param {string?} clientId
 */
export function useMedicalCenterAdvances(centerId, clientId) {
  return useQuery({
    queryKey: ["advances", centerId, { client: clientId }],
    queryFn: () => getAdvancesFromMedicalCenter(centerId, clientId),
  });
}

/**
 * Hook to get list of available diagnosis
 */
export function useDiagnosisList() {
  return useQuery({
    queryKey: ["diagnosisList"],
    queryFn: () => getDiagnosisList(),
    staleTime: Infinity,
    select: (data) =>
      data.map((diagnosis) => ({
        id: diagnosis.id,
        name: `${diagnosis.code} - ${diagnosis.name}`,
      })),
  });
}

/**
 * Hook to get list of scales
 * @todo filter by insurance company
 */
export function useScales() {
  return useQuery({
    queryKey: ["scales"],
    queryFn: () => getScales(),
  });
}

/**
 * Hook to get requirements associated with a scale, can filter by requirement type
 * @param {number?} clientId
 * @param {number?} requirementType
 */
export function useScaleRequirements(clientId, requirementType) {
  return useQuery({
    queryKey: ["scaleRequirements", clientId, { requirementType }],
    queryFn: () => getScaleRequirements(clientId, requirementType),
    enabled: clientId !== null && requirementType !== null,
  });
}

/**
 * Hook to get requirements for editing a scale
 * @param {object} params
 * @param {number} params.clientId
 * @param {import("@tanstack/react-query").UseQueryOptions} params.options
 */
export function useScaleRequirementsForEdit({
  clientId,
  medicalCenter = null,
  options,
}) {
  return useQuery({
    queryKey: ["scaleRequirements", clientId, { edit: true, medicalCenter }],
    queryFn: () => getScaleRequirementsForEdit(clientId, medicalCenter),
    ...options,
  });
}

/**
 * Hook to get the edit history of scale requirements
 * @author gabrielm
 * @param {object} params
 * @param {number?} params.clientId
 * @param {number?} params.medicalCenterId
 */
export function useScaleRequirementsEditHistory({ clientId, medicalCenterId }) {
  return useQuery({
    queryKey: ["scaleRequirements", "history", { clientId, medicalCenterId }],
    queryFn: () => getScaleRequirementsHistory({ clientId, medicalCenterId }),
    enabled: !!clientId && !!medicalCenterId,
  });
}

/**
 * Hook to get list of clients
 * @param {boolean | undefined} scaleNull
 */
export function useClients(scaleNull, fundType) {
  return useQuery({
    queryKey: ["clients"],
    queryFn: () => getClients(scaleNull, fundType),
  });
}

/**
 * Hook to get client by ID
 * @author gabrielm
 * @param {number?} clientId
 */
export function useClient(clientId) {
  return useQuery({
    queryKey: ["client", clientId],
    queryFn: () => getClient(clientId),
    enabled: !!clientId,
  });
}

/**
 * Hook to get list of subsidiaries
 */
export function useSubsidiaries() {
  return useQuery({
    queryKey: ["subsidiaries"],
    queryFn: () => getSubsidiaries(),
  });
}

/**
 * Hook to get list of medical centers from a list of requirements
 * @param {object} params
 * @param {number[]?} params.requirements Requirement being requested
 * @param {number?} params.client ID of the client that the patient is associated to
 * @param {number?} params.subAdministrator ID of the sub-administrator
 * @param {number?} params.state State of the medical center
 * @param {boolean} params.enabled If query should be enabled
 * @param {import("@tanstack/react-query").UseQueryOptions} params.options
 */
export function useCentersFromRequirements({
  requirements,
  client = null,
  subAdministrator = null,
  state = null,
  enabled,
  options = {},
}) {
  return useQuery({
    queryKey: [
      "medicalCenters",
      "requirements",
      { requirements, subAdministrator, client, state },
    ],
    queryFn: () =>
      getCentersFromRequirements({
        requirements,
        client,
        state,
        subAdministrator,
      }),
    enabled,
    ...options,
  });
}

/**
 * Hook to get requirements of a type offered from a medical center to a client
 * @param {object} params
 * @param {number?} params.centerId
 * @param {number?} params.client
 * @param {number?} params.requirementType
 * @param {number?} params.subAdministrator
 * @param {import("@tanstack/react-query").UseQueryOptions?} params.options
 */
export function useMedicalCenterClientRequirements({
  centerId,
  client,
  requirementType,
  state,
  subAdministrator,
  options = {},
}) {
  return useQuery({
    queryKey: [
      "medicalCenter",
      "requirements",
      Number(centerId),
      { client, requirementType, state, subAdministrator },
    ],
    queryFn: () =>
      getClientRequirementsFromCenter({
        centerId: Number(centerId),
        client: Number(client),
        requirementType: Number(requirementType),
        state: state,
        subAdministrator,
      }),
    enabled:
      centerId !== null && (client !== null || subAdministrator !== null),
    ...options,
  });
}

/**
 * Hook to get requirements of a type offered from a medical center to a client
 * @param {object} params
 * @param {number?} params.centerId
 * @param {number?} params.client
 * @param {number?} params.requirementType
 * @param {number?} params.state
 * @param {number?} params.subAdministrator
 */
export function useMedicalCenterClientRequirementsList({
  centerId,
  client,
  requirementType,
  state,
  subAdministrator = null,
}) {
  return useQuery({
    queryKey: [
      "medicalCenterList",
      "requirements",
      Number(centerId),
      { client, requirementType, state, subAdministrator },
    ],
    queryFn: () =>
      getClientRequirementsListFromCenter({
        centerId: Number(centerId),
        client: Number(client),
        requirementType: Number(requirementType),
        state: state,
        subAdministrator,
      }),
    enabled:
      centerId !== null && (client !== null || subAdministrator !== null),
  });
}

export function useCachedRequirementsByClientAndCenter({
  clientId,
  centerId,
  centerName,
}) {
  return useQuery({
    queryKey: [
      "cachedRequirementsByClientAndCenter",
      clientId,
      centerId,
      centerName,
    ],
    queryFn: () =>
      getCachedRequirementsByClientAndCenter({
        clientId: Number(clientId),
        centerId: Number(centerId),
        centerName,
      }),
    enabled: clientId !== null && centerId !== null,
    cacheTime: 0, // Disable caching to always fetch fresh data
  });
}

export function useClientList() {
  return useQuery({
    queryKey: ["clientList"],
    queryFn: () => getClientList(),
    select: (data) => {
      return data?.data.map((client) => ({
        id: client.id,
        name: client.name,
      }));
    },
  });
}

/**
 * Hook to get the current dollar rate
 *
 */
export function useCurrentDollarRate() {
  return useQuery({
    queryKey: ["dollarRate"],
    queryFn: () => getCurrentDollarRate(),
    // select: (data) => data.oficial.value,
    // staleTime: 1000 * 60 * 60,
  });
}

export function useGetInsurance() {
  const role = localStorage.getItem("role");
  return useQuery({
    queryKey: ["insurance"],
    queryFn: () => getInsurance(),
    staleTime: Infinity,
    enabled: role !== UserRole.MEDICAL_CENTER,
  });
}

export function useUsersList() {
  return useQuery({
    queryKey: ["users"],
    queryFn: () => getusersList(),
    staleTime: Infinity,
  });
}

export function useUsersClientList(userID) {
  return useQuery({
    queryKey: ["usersClient", userID],
    queryFn: () => getUsersClientList(userID),
    staleTime: Infinity,
    enabled: !!userID,
  });
}

export function useStateLocations() {
  return useQuery({
    queryKey: ["stateLocations"],
    queryFn: () => getStateLocations(),
  });
}

/**
 * `useQuery` hook to get managed medical centers
 * @author gabrielm
 */
export function useManagedMedicalCenters() {
  return useQuery({
    queryKey: ["managedMedicalCenters"],
    queryFn: () => getManagedMedicalCenters(),
  });
}

/**
 * `useQuery` hook to get medical case status history
 * @author gabrielm
 * @param {number} caseId
 * @param {import("@tanstack/react-query").UseQueryOptions} options
 */
export function useMedicalCaseStatusHistory(caseId, options = {}) {
  return useQuery({
    queryKey: ["medicalCaseStatusHistory", caseId],
    queryFn: () => getMedicalCaseStatusHistory(caseId),
    ...options,
  });
}

/**
 * Hook to get all doctors
 */
export function useCitaMedicaDoctors() {
  return useQuery({
    queryKey: ["citaMedicaDoctors"],
    queryFn: () => getCitaMedicaDoctors(),
    select: (data) => {
      const uniqueDoctors = new Map();

      data.forEach((doctor) => {
        if (!uniqueDoctors.has(doctor.id)) {
          const averageRating = doctor.average_rating;
          const specialties = doctor.specialities.map((speciality) => ({
            id: speciality.id,
            name: speciality.name,
          }));
          const profileImage = doctor.url_profile_image;

          doctor.is_assigned =
            doctor.is_assigned === true || doctor.is_assigned === "yes";

          uniqueDoctors.set(doctor.id, {
            averageRating,
            specialties,
            profileImage,
            ...doctor,
          });
        }
      });

      const results = Array.from(uniqueDoctors.values());
      const count = results.length;

      return {
        count,
        results,
      };
    },
  });
}

export function useCitaMedicaSpecialties() {
  return useQuery({
    queryKey: ["citaMedicaSpecialties"],
    queryFn: () => getCitaMedicaSpecialties(),
    select: (data) => {
      return data.map((specialty) => ({
        requirementId: String(specialty.id),
        label: String(specialty.name),
        value: String(specialty.specialty_id),
        specialtyName: String(specialty.specialty_name),
      }));
    },
  });
}

/**
 * Hook to get doctors grouped by their category or specialty
 * @param {Array<number>} specialties - List of specialty IDs to filter doctors
 */
export function useConsultDoctorsQuery(specialties) {
  return useQuery({
    queryKey: ["consultDoctors", specialties],
    queryFn: () => getConsultDoctors(specialties),
    enabled: !!specialties?.length, // Only fetch if specialties are provided
    select: (response) => {
      // Remove duplicate doctors based on their ID
      const uniqueDoctors = new Map();
      response.data.forEach((doctor) => {
        if (!uniqueDoctors.has(doctor.id)) {
          uniqueDoctors.set(doctor.id, doctor);
        }
      });

      // Group doctors by their specialties
      const groupedDoctors = {};
      Array.from(uniqueDoctors.values()).forEach((doctor) => {
        doctor.specialities.forEach((specialty) => {
          if (!groupedDoctors[specialty.id]) {
            groupedDoctors[specialty.id] = {
              specialtyName: specialty.name,
              doctors: [],
            };
          }
          groupedDoctors[specialty.id].doctors.push(doctor);
        });
      });

      return groupedDoctors;
    },
  });
}

/**
 * React Query hook to fetch bookings for a medical case.
 * @param {number} medicalCaseId - The ID of the medical case.
 * @returns {object} - React Query result object.
 */
export const useCitaMedicaBookings = (medicalCaseId) => {
  return useQuery({
    queryKey: ["bookings", medicalCaseId],
    queryFn: () => getCitaMedicaBookings(medicalCaseId),
    enabled: !!medicalCaseId, // Only fetch if medicalCaseId is provided
    select: (response) => {
      return response.data;
    },
  });
};

/**
 * Hook to get the billing list
 * @param {object} params
 * @param {number} params.clientId
 * @param {number?} params.state
 * @param {import("@tanstack/react-query").UseQueryOptions} params.options
 */
export function useBillingList({
  clientId,
  state = null,
  period = null,
  medicalCenter = null,
  options = {},
}) {
  return useQuery({
    queryKey: ["billingList", clientId, { state, period, medicalCenter }],
    queryFn: () =>
      getBillingList({ clientID: clientId, state, period, medicalCenter }),
    ...options,
  });
}

/**
 * Hook to get billing amount in Bs for a specific client
 * @param {number} clientId
 */
export function useBillingAmountBs({ billingID }) {
  return useQuery({
    queryKey: ["billingAmountBs", billingID],
    queryFn: () => getBillingAmountBs(billingID),
    enabled: !!billingID,
  });
}

/**
 * Hook to get details of a specific billing
 * @param {number} billingId
 */
export function useGetBillingDetail({ billingID }) {
  return useQuery({
    queryKey: ["billingDetail", billingID],
    queryFn: () => getBillingDetail(billingID),
    enabled: !!billingID,
  });
}

/**
 * Hook to get profile picture
 * @param {object} params
 * @param {string?} params.token
 */
export function useProfilePicture({ token }) {
  return useQuery({
    queryKey: ["profilePicture", token],
    queryFn: () => getProfilePicture(),
    enabled: !!token,
  });
}

/**
 * Hook to fetch chat messages for a specific chat
 * @param {object} params
 * @param {number} params.chatId - ID of the chat to fetch messages for
 * @param {import("@tanstack/react-query").UseInfiniteQueryOptions} params.options - Additional options for the query
 */
export function useChatMessages({ chatId, options }) {
  return useInfiniteQuery({
    queryKey: ["chatMessages", Number(chatId)],
    queryFn: ({ pageParam = 1 }) =>
      getChatMessages({ chatId, page: pageParam }),
    getNextPageParam: (lastPage, pages) => lastPage.nextPage,
    ...options,
  });
}

/**
 * Hook to fetch if current medical center user is insolvent
 * @param {import("@tanstack/react-query").UseQueryOptions?} options
 */
export function useIsInsolvent(options = {}) {
  return useQuery({
    queryKey: ["isInsolvent"],
    queryFn: () => getIsInsolvent(),
    ...options,
  });
}

/**
 * Hook to check if a medical center can add requirements to a case
 * @param {import("@tanstack/react-query").UseQueryOptions} options
 */
export function useCanAddRequirementsToCase(medicalCenter, options = {}) {
  return useQuery({
    queryKey: ["canAddRequirementsToCase", medicalCenter],
    queryFn: () => getCanAddRequirementsToCase(medicalCenter),
    ...options,
  });
}

/**
 * Hook to fetch billing statistics for a specific month
 * @param {string?} month
 * @param {import("@tanstack/react-query").UseQueryOptions} options
 */
export function useBillingStats({ client, month }, options = {}) {
  return useQuery({
    queryKey: ["billingStats", { client, month }],
    queryFn: () => getBillingStats(client, month),
    ...options,
  });
}

/**
 * Hook to fetch billing history for a specific billing ID
 * @author gabrielm
 * @param {number} billingId
 * @param {import("@tanstack/react-query").UseQueryOptions} options
 */
export function useBillingHistory(billingId, options = {}) {
  return useQuery({
    queryKey: ["billingHistory", billingId],
    queryFn: () => getBillingHistory(billingId),
    ...options,
  });
}

/**
 * Hook to fetch pending requests
 * @author gabrielm
 * @param {object} params
 */
export function usePendingRequests({ page, pageSize, filters }) {
  return useQuery({
    queryKey: ["medicalCases", page, pageSize, filters],
    queryFn: () => getPendingRequests(page, pageSize, filters),
    select: (data) => ({
      count: data.count,
      results: data.results,
    }),
  });
}

/**
 * Hook to fetch dependency insured by CI type and number
 * @author gabrielm
 * @param {string} ciType
 * @param {string} ciNumber
 * @param {string} subsidiaryId
 * @param {import("@tanstack/react-query").UseQueryOptions} options
 */
export function useDependencyInsured(
  ciType,
  ciNumber,
  subsidiaryId,
  options = {}
) {
  return useQuery({
    queryKey: ["dependencyInsured", ciType, ciNumber, subsidiaryId],
    queryFn: () => getDependencyInsured(ciType, ciNumber, subsidiaryId),
    enabled: !!ciType && !!ciNumber && !!subsidiaryId,
    ...options,
  });
}

/**
 * Hook to check if a user can add requirements to a case in call center
 * @author gabrielm
 * @param {string | undefined} userId
 * @param {string} clientId
 */
export function useCanAddRequirementsCallCenter(userId, clientId, options = {}) {
  return useQuery({
    queryKey: ["canAddRequirementsCallCenter", userId, clientId],
    queryFn: () => canAddRequirementsToCaseCallCenter(userId, clientId),
    retry: 2,
    ...options,
  });
}
